/**
 * Buffer circular de los ultimos ~2s de juego para repetir el gol.
 * Solo afecta posiciones visuales durante la fase `goal`; no toca la fisica.
 */
import {
  MATCH,
  OFFSIDE,
  PHYSICS,
  type Ball,
  type MatchState,
  type Player,
  type Possession,
} from "@golazo/shared";
import { copyInto } from "../core/math.js";

/**
 * Cuanto llevamos de repeticion: 0 mientras se juega o se celebra, y de 0 a 1
 * durante el clip.
 *
 * Vive aqui, con el resto de la maquinaria de repeticion, porque lo consultan
 * tres sitios —la camara, el encuadre y el HUD— y tener la condicion escrita
 * tres veces es la manera de que un dia dejen de estar de acuerdo sobre cuando
 * empieza el clip.
 */
export function replayProgress(state: MatchState): number {
  if (state.phase === "offside") return varReplayProgress(state);
  if (state.phase !== "goal") return 0;
  // La cuenta atras del saque es la COLA de `goalPause`, asi que el clip ya no
  // llega hasta cero: descontarla es lo que impide que la repeticion siga
  // "reproduciendose" mientras en pantalla se cuenta 3-2-1.
  const left = state.goalPause - MATCH.RESTART_COUNTDOWN;
  if (left <= 0 || left > MATCH.REPLAY_PLAYBACK) return 0;
  return Math.min(1, Math.max(0, 1 - left / MATCH.REPLAY_PLAYBACK));
}

/**
 * Segundos que quedan de cuenta atras antes de reanudar; 0 fuera de ella.
 *
 * Vive aqui con el resto de tramos de la pausa por gol, y por el mismo motivo:
 * lo consultan la simulacion (para congelar) y el HUD (para pintar el numero), y
 * dos copias de la misma condicion acaban discrepando el dia que alguien mueva
 * una constante.
 */
export function restartCountdown(state: MatchState): number {
  if (state.phase !== "goal") return 0;
  if (state.goalPause > MATCH.RESTART_COUNTDOWN) return 0;
  return Math.max(0, state.goalPause);
}

/**
 * Los tres tramos de la revision de VAR, en el orden en que se ven.
 *
 * `goalPause` cuenta hacia atras desde `OFFSIDE.PAUSE`, asi que la cuenta va al
 * reves de como se lee: primero el aviso (queda mucho), luego la repeticion, y
 * el congelado al final (queda poco). Vive aqui, con el resto de la maquinaria
 * de repeticion, porque lo consultan cinco sitios —simulacion, HUD, camara,
 * encuadre y la capa de la linea— y tener la condicion escrita cinco veces es
 * la manera de que un dia dejen de estar de acuerdo.
 */
export type VarStage = "whistle" | "replay" | "freeze" | "none";

export function varStage(state: MatchState): VarStage {
  if (state.phase !== "offside") return "none";
  if (state.goalPause > OFFSIDE.VAR_REPLAY + OFFSIDE.VAR_FREEZE) return "whistle";
  if (state.goalPause > OFFSIDE.VAR_FREEZE) return "replay";
  return "freeze";
}

/** 0 → 1 dentro del tramo de repeticion; 0 fuera de el. */
export function varReplayProgress(state: MatchState): number {
  if (varStage(state) !== "replay") return 0;
  const left = state.goalPause - OFFSIDE.VAR_FREEZE;
  return Math.min(1, Math.max(0, 1 - left / OFFSIDE.VAR_REPLAY));
}

/** 0 → 1 dentro del congelado con la linea trazada; 0 fuera de el. */
export function varFreezeProgress(state: MatchState): number {
  if (varStage(state) !== "freeze") return 0;
  return Math.min(1, Math.max(0, 1 - state.goalPause / OFFSIDE.VAR_FREEZE));
}

/**
 * Lo que se VE de cada jugador y no es donde esta: el aro que se enciende al
 * pulsar golpeo, la carga, el casco al esprintar y los arcos de recarga.
 *
 * La repeticion movia a los jugadores con lo grabado pero pintaba todo esto EN
 * VIVO: lo que pulsaras mientras la veias se encendia encima del clip, y lo
 * que habias hecho en la jugada no salia. Se graba aparte del estado de la
 * simulacion y el render lo lee de aqui durante el clip: repetirlo no toca
 * nada que cuente para el partido (ni para el online).
 */
export const LOOK = {
  /** Boton de golpeo mantenido (1/0). */
  HELD: 0,
  /** Segundos desde la ultima pulsacion de golpeo (el fogonazo). */
  SINCE: 1,
  CHARGE: 2,
  /** Cargando de verdad (`chargeSteer`, 1/0). */
  STEER: 3,
  BURST: 4,
  DASH_CD: 5,
  TACKLE_CD: 6,
} as const;
export const LOOK_FLOATS = 7;

/** Lo que cada jugador tenia pulsado del golpeo, en el orden de `players`. */
export interface KickTrack {
  held: Uint8Array;
  since: Float32Array;
}

export interface ReplaySnapshot {
  ball: {
    x: number;
    y: number;
    vx: number;
    vy: number;
    spin: number;
    topSpin: number;
    loft: number;
    loftVel: number;
  };
  players: Float32Array;
  /** `LOOK_FLOATS` por jugador. Ver `LOOK`. */
  look: Float32Array;
  ownerId: string | null;
}

const PLAYER_FLOATS = 6;

/**
 * Congela el estado tal cual esta ahora.
 *
 * Exportado porque el VAR lo necesita fuera del buffer circular: durante la
 * revision se mueve a todo el mundo al instante del pase, y al terminar hay que
 * devolverlos a donde estaban cuando se pito — si no, el saque se juega desde
 * unas posiciones de hace tres segundos.
 */
export function captureSnapshot(
  players: readonly Player[],
  ball: Ball,
  possession: Possession,
): ReplaySnapshot {
  return capture(players, ball, possession);
}

export function restoreSnapshot(
  snap: ReplaySnapshot,
  players: readonly Player[],
  ball: Ball,
  possession: Possession,
): void {
  applySnapshot(snap, players, ball, possession);
}

function capture(
  players: readonly Player[],
  ball: Ball,
  possession: Possession,
  kicks?: KickTrack,
): ReplaySnapshot {
  const data = new Float32Array(players.length * PLAYER_FLOATS);
  const look = new Float32Array(players.length * LOOK_FLOATS);
  for (let i = 0; i < players.length; i++) {
    const p = players[i]!;
    const o = i * PLAYER_FLOATS;
    data[o] = p.pos.x;
    data[o + 1] = p.pos.y;
    data[o + 2] = p.vel.x;
    data[o + 3] = p.vel.y;
    data[o + 4] = p.facing.x;
    data[o + 5] = p.facing.y;
    const l = i * LOOK_FLOATS;
    look[l + LOOK.HELD] = kicks?.held[i] ?? 0;
    look[l + LOOK.SINCE] = kicks?.since[i] ?? 99;
    look[l + LOOK.CHARGE] = p.charge;
    look[l + LOOK.STEER] = p.chargeSteer ? 1 : 0;
    look[l + LOOK.BURST] = p.burstTime;
    look[l + LOOK.DASH_CD] = p.dashCooldown;
    look[l + LOOK.TACKLE_CD] = p.tackleCooldown;
  }
  return {
    ball: {
      x: ball.pos.x,
      y: ball.pos.y,
      vx: ball.vel.x,
      vy: ball.vel.y,
      spin: ball.spin,
      topSpin: ball.topSpin,
      loft: ball.loft,
      loftVel: ball.loftVel,
    },
    players: data,
    look,
    ownerId: possession.ownerId,
  };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function applySnapshot(
  snap: ReplaySnapshot,
  players: readonly Player[],
  ball: Ball,
  possession: Possession,
): void {
  for (let i = 0; i < players.length; i++) {
    const p = players[i]!;
    const o = i * PLAYER_FLOATS;
    copyInto(p.prevPos, p.pos);
    p.pos.x = snap.players[o]!;
    p.pos.y = snap.players[o + 1]!;
    p.vel.x = snap.players[o + 2]!;
    p.vel.y = snap.players[o + 3]!;
    p.facing.x = snap.players[o + 4]!;
    p.facing.y = snap.players[o + 5]!;
  }

  copyInto(ball.prevPos, ball.pos);
  ball.pos.x = snap.ball.x;
  ball.pos.y = snap.ball.y;
  ball.vel.x = snap.ball.vx;
  ball.vel.y = snap.ball.vy;
  ball.spin = snap.ball.spin;
  ball.topSpin = snap.ball.topSpin;
  ball.loft = snap.ball.loft;
  ball.prevLoft = snap.ball.loft;
  ball.loftVel = snap.ball.loftVel;
  possession.ownerId = snap.ownerId;
}

function blendSnapshots(
  a: ReplaySnapshot,
  b: ReplaySnapshot,
  t: number,
  players: readonly Player[],
  ball: Ball,
  possession: Possession,
): void {
  for (let i = 0; i < players.length; i++) {
    const p = players[i]!;
    const o = i * PLAYER_FLOATS;
    copyInto(p.prevPos, p.pos);
    p.pos.x = lerp(a.players[o]!, b.players[o]!, t);
    p.pos.y = lerp(a.players[o + 1]!, b.players[o + 1]!, t);
    p.vel.x = lerp(a.players[o + 2]!, b.players[o + 2]!, t);
    p.vel.y = lerp(a.players[o + 3]!, b.players[o + 3]!, t);
    p.facing.x = lerp(a.players[o + 4]!, b.players[o + 4]!, t);
    p.facing.y = lerp(a.players[o + 5]!, b.players[o + 5]!, t);
  }

  copyInto(ball.prevPos, ball.pos);
  ball.pos.x = lerp(a.ball.x, b.ball.x, t);
  ball.pos.y = lerp(a.ball.y, b.ball.y, t);
  ball.vel.x = lerp(a.ball.vx, b.ball.vx, t);
  ball.vel.y = lerp(a.ball.vy, b.ball.vy, t);
  ball.spin = lerp(a.ball.spin, b.ball.spin, t);
  ball.topSpin = lerp(a.ball.topSpin, b.ball.topSpin, t);
  ball.loft = lerp(a.ball.loft, b.ball.loft, t);
  ball.prevLoft = ball.loft;
  ball.loftVel = lerp(a.ball.loftVel, b.ball.loftVel, t);
  possession.ownerId = t < 0.5 ? a.ownerId : b.ownerId;
}

export class ReplayBuffer {
  private readonly maxFrames: number;
  private readonly frames: ReplaySnapshot[] = [];

  /*
   * Lo que pasa DESPUES de que el balon cruce la linea.
   *
   * El clip normal (`frames`) es un buffer circular de los ultimos 2,5 s y
   * termina en el instante del gol. La reproduccion dura 5,2 s, y el ultimo 34%
   * —1,77 segundos— se quedaba clavado en ese ultimo fotograma: la repeticion
   * se paraba en seco justo al entrar el balon. Se llamaba "camara lenta" pero
   * no lo era, era una imagen fija.
   *
   * Esos 1,77 s se llenan ahora con juego de verdad. Da tiempo de sobra porque
   * la celebracion (2,9 s) ocurre ANTES de la repeticion: cuando toca
   * reproducir, la cola ya esta grabada entera.
   *
   * Va en su propia lista y NO circular: interesan los primeros instantes
   * despues del gol, no los ultimos. Si se desbordara por la cabeza como
   * `frames`, se veria el final de la celebracion y no el principio.
   */
  private readonly tail: ReplaySnapshot[] = [];
  private readonly maxTail: number;
  /** El fotograma que se esta enseñando: de el sale el aspecto (`look`). */
  private shown: ReplaySnapshot | null = null;

  constructor(durationSec = MATCH.REPLAY_CLIP) {
    this.maxFrames = Math.ceil(durationSec * PHYSICS.TICK_RATE);
    // Tantos fotogramas como segundos vaya a ocupar en la reproduccion, para
    // que la cola salga a velocidad normal y no a camara rapida.
    this.maxTail = Math.ceil(
      MATCH.REPLAY_PLAYBACK * MATCH.REPLAY_SLOWMO_TAIL * PHYSICS.TICK_RATE,
    );
  }

  record(players: readonly Player[], ball: Ball, possession: Possession, kicks?: KickTrack): void {
    this.frames.push(capture(players, ball, possession, kicks));
    if (this.frames.length > this.maxFrames) this.frames.shift();
  }

  /**
   * El aspecto de los jugadores en el fotograma que se esta enseñando (ver
   * `LOOK`). El mas cercano, no una mezcla: un fogonazo o un "pulsado" a
   * medias no existen.
   */
  shownLook(): Float32Array | null {
    return this.shown?.look ?? null;
  }

  /** Empieza a grabar la cola. Se llama en el tick del gol. */
  beginTail(): void {
    this.tail.length = 0;
  }

  /** Graba la celebracion, hasta llenar la cola y ni un fotograma mas. */
  recordTail(players: readonly Player[], ball: Ball, possession: Possession, kicks?: KickTrack): void {
    if (this.tail.length >= this.maxTail) return;
    this.tail.push(capture(players, ball, possession, kicks));
  }

  /** ¿Hay celebracion grabada que enseñar? Con dos ya se puede interpolar. */
  hasTail(): boolean {
    return this.tail.length > 1;
  }

  /** Reproduce la cola: `progress` de 0 (el gol) a 1 (final de lo grabado). */
  applyTail(
    progress: number,
    players: readonly Player[],
    ball: Ball,
    possession: Possession,
  ): void {
    if (this.tail.length === 0) return;
    if (this.tail.length === 1) {
      this.shown = this.tail[0]!;
      applySnapshot(this.tail[0]!, players, ball, possession);
      return;
    }
    const f = Math.min(1, Math.max(0, progress)) * (this.tail.length - 1);
    const i0 = Math.floor(f);
    const i1 = Math.min(i0 + 1, this.tail.length - 1);
    this.shown = f - i0 < 0.5 ? this.tail[i0]! : this.tail[i1]!;
    blendSnapshots(this.tail[i0]!, this.tail[i1]!, f - i0, players, ball, possession);
  }

  /** Reproduce el clip desde hace ~REPLAY_CLIP s hasta el momento del gol. */
  applyAt(
    clipTime: number,
    players: readonly Player[],
    ball: Ball,
    possession: Possession,
  ): void {
    if (this.frames.length === 0) return;
    if (this.frames.length === 1) {
      this.shown = this.frames[0]!;
      applySnapshot(this.frames[0]!, players, ball, possession);
      return;
    }

    const progress = Math.min(1, Math.max(0, clipTime / MATCH.REPLAY_CLIP));
    const f = progress * (this.frames.length - 1);
    const i0 = Math.floor(f);
    const i1 = Math.min(i0 + 1, this.frames.length - 1);
    const t = f - i0;
    this.shown = t < 0.5 ? this.frames[i0]! : this.frames[i1]!;
    blendSnapshots(this.frames[i0]!, this.frames[i1]!, t, players, ball, possession);
  }
}
